import {
    EntityTemplateField,
    EntityTemplateStep,
    getStandardFieldTranslationKey,
    isSystemStandardField,
    resolveSupplierPermissions,
    SystemStandardField,
    SystemSupplierPermissions,
} from "../../entityTemplates/EntityTemplate.types.ts";
import { SystemModel, SystemSupplierViewModel } from "./SystemEdit.types.ts";
import { ValidationError } from "../../common/validationError.ts";
import { useTranslation } from "../../localization/useTranslation.ts";
import { isNullOrWhitespace } from "../../common/stringOperations.ts";
import { useSelectableLegalEntityQuery } from "../../legalEntity/useSelectableLegalEntityQuery.ts";
import { isTransferBasisMissing, isTransferImpactAssessmentMissing } from "../../common/dataProcessorValidation.ts";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider.tsx";
import { useResponsibleQuery } from "../../user/hooks/useResponsibleQuery.ts";
import { exhaustiveGuard } from "../../common/utilities.ts";
import { useTagsMultiSelectAutoUpdater } from "../../common/components/tagsMultiSelect/tagsMultiSelectAutoUpdater/TagsMultiSelectAutoUpdater.hooks.ts";
import { TagRelationType } from "../../common/components/tagsMultiSelect/TagsMultiSelect.types.ts";
import { SelectableSecurityMeasure } from "../../processingActivity/ProcessingActivity.types.ts";
import { useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery.ts";
import { hasFieldData } from "../../entityTemplates/fieldValue.ts";

export function useValidateSystemStep(systemId: string) {
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();
    const { selectedTags } = useTagsMultiSelectAutoUpdater(systemId, TagRelationType.System, false);
    const legalEntitiesQuery = useSelectableLegalEntityQuery();
    const { userData } = useResponsibleQuery();
    const selectableSecurityMeasuresQuery = useDotLegalQueryWithKey<Array<SelectableSecurityMeasure>>(
        "systemSecurityMeasures" + systemId,
        `/securitymeasures/${systemId}/system`,
        { enabled: !!systemId }
    );
    const hasInheritedSecurityMeasures = selectableSecurityMeasuresQuery.data?.some((x) => x.isInherited) ?? false;

    function validateStep(step: EntityTemplateStep, system: SystemModel) {
        const result: Array<ValidationError> = [];

        step.fields.forEach((field) => {
            if (field.standardFieldIdentifier === SystemStandardField.SystemSuppliers) {
                const supplierErrors = validateSuppliers(system.suppliers, resolveSupplierPermissions(field.subPermissions));
                supplierErrors.forEach((error) => result.push(error));
            }

            if (field.standardFieldIdentifier === SystemStandardField.SystemSecurityMeasures && hasInheritedSecurityMeasures) {
                return;
            }

            const fieldValidation = validateValue(getSystemFieldValue(field, system, selectedTags), field, system);
            if (fieldValidation) {
                result.push(fieldValidation);
            }
        });

        return result;
    }

    function validateValue(value: any, field: EntityTemplateField, system: SystemModel): ValidationError | null {
        const fieldName = field.standardFieldIdentifier ? SystemStandardField[field.standardFieldIdentifier].toString() : field.id!;
        const hasValue = hasFieldData(value);

        if (field.required && !hasValue) {
            const errorMessage = getRequiredErrorMessage(field.standardFieldIdentifier);
            return new ValidationError(fieldName, errorMessage);
        }

        return fieldCustomValidation(value, fieldName, system, field);
    }

    function getRequiredErrorMessage(standardFieldIdentifier: number | null | undefined) {
        switch (standardFieldIdentifier) {
            case SystemStandardField.SystemSuppliers:
                return translateString("supplierRequired");
            case SystemStandardField.SystemPolicies:
                return translateString("policiesRequired");
            case SystemStandardField.SystemDataCategories:
                return translateString("dataCategoriesRequired");
            case SystemStandardField.SystemAssociatedAssets:
                return translateString("associatedAssetRequired");
            default:
                return translateString("requiredField");
        }
    }

    function fieldCustomValidation(value: any, fieldName: string, system: SystemModel, field: EntityTemplateField): ValidationError | null {
        if (field.standardFieldIdentifier === SystemStandardField.SystemName) {
            if (isNullOrWhitespace(value)) {
                return new ValidationError("systemName", translateString("nameRequired"));
            }
        }

        if (field.standardFieldIdentifier === SystemStandardField.SystemResponsible) {
            const user = userData?.find((x) => x.id === system.responsibleId);
            if (user && !user.active) {
                return new ValidationError("inActiveUserAffiliation", translateString("userDeactivated"));
            }
        }

        return null;
    }

    function validateSuppliers(suppliers: Array<SystemSupplierViewModel>, supplierPermissions: SystemSupplierPermissions): Array<ValidationError> {
        const result: Array<ValidationError> = [];
        const legalEntities = legalEntitiesQuery.data ?? [];

        suppliers.forEach((supplier) => {
            const legalEntityId = legalEntities.find((x) => x.id === supplier.legalEntityId);

            if (supplier.isSupplierADataProcessor && supplierPermissions.hasTiaAndTransferBasis) {
                if (isTransferBasisMissing(legalEntityId, supplier.transferBasisId, true)) {
                    result.push(new ValidationError(`${supplier.id}transferBasisId`, translateString("transferBasisRequired")));
                }

                if (
                    isTransferImpactAssessmentMissing(
                        legalEntityId,
                        supplier.hasTransferImpactAssessment,
                        supplier.transferImpactAssessmentId,
                        true,
                        supplier.transferBasisId,
                        supplier.containsNotAccessibleTia
                    )
                ) {
                    result.push(new ValidationError(`${supplier.id}transferImpactAssessment`, translateString("tiaRequired")));
                }
            }

            if (supplierPermissions.hasSubProcessorPermission) {
                supplier.secondarySuppliers
                    .filter((x) => !x.isLink)
                    .forEach((secondarySupplier) => {
                        const dataProcessor = legalEntities.find((x) => x.id === secondarySupplier.dataProcessorId);

                        if (isNullOrWhitespace(secondarySupplier.dataProcessorId)) {
                            result.push(
                                new ValidationError(`${supplier.id}${secondarySupplier.id}dataProcessorId`, translateString("secondarySupplierRequired"))
                            );
                        }

                        if (secondarySupplier.dataProcessorId === supplier.legalEntityId && supplier.legalEntityId) {
                            result.push(
                                new ValidationError(`${supplier.id}${secondarySupplier.id}dataProcessorId`, translateString("secondarySupplierUsedAsSupplier"))
                            );
                        }

                        if (
                            supplier.secondarySuppliers.filter((entry) => entry.dataProcessorId === secondarySupplier.dataProcessorId).length > 1 &&
                            secondarySupplier.dataProcessorId
                        ) {
                            result.push(
                                new ValidationError(`${supplier.id}${secondarySupplier.id}dataProcessorId`, translateString("duplicateSecondarySupplier"))
                            );
                        }

                        if (secondarySupplier.isSubDataProcessor && supplierPermissions.hasTiaAndTransferBasis) {
                            if (isTransferBasisMissing(dataProcessor, secondarySupplier.transferBasisId, true)) {
                                result.push(
                                    new ValidationError(`${supplier.id}${secondarySupplier.id}transferBasisId`, translateString("transferBasisRequired"))
                                );
                            }

                            if (
                                isTransferImpactAssessmentMissing(
                                    dataProcessor,
                                    secondarySupplier.hasTransferImpactAssessment,
                                    secondarySupplier.transferImpactAssessmentId,
                                    true,
                                    secondarySupplier.transferBasisId,
                                    secondarySupplier.containsNotAccessibleTia
                                )
                            ) {
                                result.push(
                                    new ValidationError(`${supplier.id}${secondarySupplier.id}transferImpactAssessment`, translateString("tiaRequired"))
                                );
                            }
                        }
                    });
            }
        });

        return result;
    }

    return {
        validateStep,
    };
}

export function getSystemFieldValue(field: EntityTemplateField, model: SystemModel | undefined, selectedTags: Array<string>) {
    if (field.standardFieldIdentifier && isSystemStandardField(field.standardFieldIdentifier)) {
        switch (field.standardFieldIdentifier) {
            case SystemStandardField.SystemName:
                return model?.name;
            case SystemStandardField.SystemDescription:
                return model?.description;
            case SystemStandardField.SystemResponsible:
                return model?.responsibleId;
            case SystemStandardField.DistributionArea:
                return model?.distributionArea;
            case SystemStandardField.SystemEntityType:
                return model?.entityType;
            case SystemStandardField.SystemStorageType:
                return model?.storageType;
            case SystemStandardField.SystemDeletionPeriod:
                return model?.deletionPeriodId;
            case SystemStandardField.SystemNetwork:
                return model?.network;
            case SystemStandardField.SystemIsCriticalForOperation:
                return model?.isCriticalForOperation;
            case SystemStandardField.SystemUsesAI:
                return model?.usesAI;
            case SystemStandardField.SystemBusinessAreas:
                return model?.businessAreas;
            case SystemStandardField.SystemSuppliers:
                return model?.suppliers;
            case SystemStandardField.SystemCertifications:
                return model?.certifications;
            case SystemStandardField.SystemDataStorageLocations:
                return model?.dataStorageLocations;
            case SystemStandardField.SystemSupportAccessLocations:
                return model?.supportAccessLocations;
            case SystemStandardField.SystemSecurityMeasures:
                return model?.securityMeasures;
            case SystemStandardField.SystemPolicies:
                return model?.policies;
            case SystemStandardField.SystemDataTypes:
                return model?.dataTypes;
            case SystemStandardField.SystemTags:
                return selectedTags;
            case SystemStandardField.SystemAssociatedAssets:
                return model?.disableSystemRelations || model?.hasSystemRelations;
            case SystemStandardField.SystemDataSubjects:
                return model?.systemPersonalData?.systemDataSubjects ?? model?.systemPersonalData?.processingActivityDataSubjects;
            case SystemStandardField.SystemDataCategories:
                return model?.systemPersonalData?.dataCategories;
            case SystemStandardField.SystemAdditionalUserAccess:
                return model?.additionalAccessUsers;
            case SystemStandardField.SystemExcludedUsers:
                return model?.excludedUsers;
            case SystemStandardField.SystemVisibleToAllUsers:
                return model?.visibleToAllUsers;
            case SystemStandardField.SystemBusinessAreaAccess:
                return model?.accessBusinessAreas;
            case SystemStandardField.SystemGroupCompanies:
                return model?.groupEntities;
            case SystemStandardField.SystemStatus:
                return model?.status;
            default:
                return exhaustiveGuard(field.standardFieldIdentifier);
        }
    }

    return model?.customFieldValues.find((x) => x.customInputFieldId == field.customInputFieldId)?.value;
}
